import React, { useState } from "react";
import { User } from "@phosphor-icons/react";
import useLivenessCheck from "../hooks/useLivenessCheck";
import useCreateLivenessSession from "../hooks/useCreateLivenessSession";

const TestLiveness: React.FC = () => {
  const [lastResult, setLastResult] = useState<string | null>(null);

  const { createSession, isCreatingSession, sessionError } =
    useCreateLivenessSession();

  const [session, setSession] = useState<{
    sessionId: string;
    sessionToken: string;
  } | null>(null);

  const liveness = useLivenessCheck({
    sessionId: session?.sessionId ?? "",
    sessionToken: session?.sessionToken ?? "",
    allowAudio: true,
    user: {
      firstName: "Test",
      lastName: "User",
      email: "test@visaro.ng",
    },
    onSuccess: (data) => {
      console.log("Mock Liveness Success payload:", data);
      setLastResult("Success! Camera closed and data was returned from YouVerify properly.");
    },
  });

  const handleStart = async () => {
    setLastResult(null);
    try {
      const newSession = await createSession();
      setSession(newSession);
      setTimeout(() => liveness.start(), 0);
    } catch {
      // sessionError is already set by the hook
    }
  };

  const displayError = sessionError || liveness.error;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="p-6 md:p-10 flex justify-center items-start w-full mt-10">
        <div className="w-full max-w-2xl border rounded-xl p-6 md:p-8 shadow-sm flex flex-col">
          <div className="p-5 bg-gray-50 rounded-full self-start">
            <User className="text-visaro-orange" size={48} weight="fill" />
          </div>
          <header className="my-6">
            <h1 className="text-2xl md:text-3xl font-bold text-deepBlue">
              Liveness TEST Environment
            </h1>
            <p className="text-gray-600 mt-2">
              This page is completely isolated from the database and will NOT update your profile. Use this to continuously debug the mobile camera interaction.
            </p>
          </header>

          {/* Troubleshooting */}
          <section className="mb-6">
            <h2 className="font-semibold text-lg mb-2">Controls</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Click "Start" below to trigger the YouVerify SDK overlay.</li>
              <li>Success or Failure responses will print directly below the button.</li>
            </ul>
          </section>

          {displayError && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {displayError}
            </div>
          )}

          {/* CTA */}
          <button
            className="p-4 px-5 w-full border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleStart}
            disabled={isCreatingSession || liveness.status === "running"}
          >
            {isCreatingSession
              ? "Preparing..."
              : liveness.status === "running"
              ? "Running SDK..."
              : "Start Test"}
          </button>

          {/* Status messages */}
          {liveness.status === "success" && lastResult && (
            <div className="mt-4 text-green-700 text-sm border border-green-200 bg-green-50 rounded p-3 font-bold">
              {lastResult}
            </div>
          )}
          {liveness.status === "failed" && (
            <div className="mt-4 text-red-700 text-sm border border-red-200 bg-red-50 rounded p-3">
              We couldn't complete your liveness verification. Please try again.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestLiveness;
